import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import MmcFile from "@mapmycustomers/shared/types/File";
import User from "@mapmycustomers/shared/types/User";

import { EntityMention } from "@app/component/EntityMentions";
import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import CoachingNote, {
  CoachingNoteAnswer,
  CoachingNoteAttachmentChip,
  CoachingNoteVisibility,
} from "@app/scene/dashboard/types/CoachingNote";

// Mirrors the inline shape of createCoachingNote.request in ../notes/actions.
export interface NotePayload {
  answers: CoachingNoteAnswer[];
  attachments?: CoachingNoteAttachmentChip[];
  categoryId?: null | number;
  coachingFileIds?: Array<CoachingFile["id"]>;
  mentions: EntityMention[];
  // Forwarded to the notes saga which uploads the files first and folds the
  // resulting CoachingFile ids into coachingFileIds before the create-note POST.
  // See createCoachingNote payload doc for the why.
  newFiles?: Array<Blob | File>;
  note: string;
  subjectUserId: User["id"];
  tempAuthor: Pick<User, "fullName" | "id" | "username">;
  tempCreatedAt: string;
  tempId: CoachingNote["id"];
  visibility: CoachingNoteVisibility;
}

// Subset of the standalone task request that the inline-task composer collects.
export interface InlineTaskPayload {
  assigneeId: User["id"];
  crmActivityTypeId: ActivityType["id"];
  dueDate: null | string;
  // Platform fileIds resolved from picker selections in the inline composer.
  fileIds?: Array<MmcFile["id"]>;
  mentions?: EntityMention[];
  name: string;
  // Files dropped into the inline composer. Uploaded by the orchestrator
  // before the task POST and folded into the activity's crm_activities.files.
  newFiles?: Array<Blob | File>;
  note?: string;
  subjectUserId: User["id"];
}

export const createCoachingNoteWithTask = createAsyncAction(
  "@dashboard/coaching/noteWithTask/create/request",
  "@dashboard/coaching/noteWithTask/create/success",
  "@dashboard/coaching/noteWithTask/create/failure"
)<
  { notePayload: NotePayload; taskPayload: InlineTaskPayload },
  { note: CoachingNote; task: Activity },
  { stage: "note" | "task" }
>();

// Opened by the orchestrator saga when the note created but the task POST failed.
export const openTaskRetryModal = createAction("@dashboard/coaching/noteWithTask/retry/open")<{
  noteId: CoachingNote["id"];
  taskPayload: InlineTaskPayload;
}>();

export const dismissTaskRetry = createAction("@dashboard/coaching/noteWithTask/retry/dismiss")();

export const retryCoachingTask = createAsyncAction(
  "@dashboard/coaching/noteWithTask/retry/request",
  "@dashboard/coaching/noteWithTask/retry/success",
  "@dashboard/coaching/noteWithTask/retry/failure"
)<void, Activity, void>();

export type CoachingNoteWithTaskActions = ActionType<
  | typeof createCoachingNoteWithTask
  | typeof dismissTaskRetry
  | typeof openTaskRetryModal
  | typeof retryCoachingTask
>;
